import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { BlogPost, Challenge, Idea, Maybe, Scalars, User } from '../../../common/core-api';
import { useFile, useMainModal } from '../../../hooks';
import { removeTags } from '../../../helpers';
import { CommentNotificationTypes } from '../../../common/enums';
import { RelativeDateWithTooltip, Tooltip } from '../../../common/components';
import * as Styled from './CommentContent.styled';

export const CommentContent = ({
  item,
  author,
  challenge,
  type,
  commentText,
  time,
  commentId,
  phaseId,
  isValid
}: {
  item?: Maybe<BlogPost | Idea>;
  author?: Maybe<User>;
  challenge?: Maybe<Challenge>;
  type?: CommentNotificationTypes;
  commentText: string;
  time: string;
  commentId?: string | null;
  phaseId?: Maybe<Scalars['String']>;
  isValid: boolean;
}) => {
  const [getFile] = useFile();
  const [openModal] = useMainModal();
  const intl = useIntl();

  if (!item || !type) {
    return null;
  }

  const message = intl.formatMessage({
    id: 'youNoLongerHaveAccessToThisItem',
    defaultMessage: 'You no longer have access to this item'
  });

  const getItemUrl = () => {
    const url =
      type &&
      [
        CommentNotificationTypes.REASON_BLOG_SINGLE,
        CommentNotificationTypes.REASON_BLOG_SAME_USER,
        CommentNotificationTypes.REASON_BLOG_DIFFERENT_USER
      ].includes(type)
        ? `/blogpost/${item.id}`
        : `/post/${item.id}`;

    return url;
  };

  const commentBodyClicked = () => {
    const url = `${getItemUrl()}?highlightPost=${commentId}`;

    window.open(url, '_blank');
  };

  const url = author?.profileImageFile && getFile({ file: author.profileImageFile, width: 30, height: 30 });
  const itemUrl = getItemUrl();

  const clearTitle = removeTags(item.title || '');
  const clearChallengeTitle = removeTags(challenge?.title || '');

  return (
    <Styled.ContentContainer caretLocation="leftTop" border="none" depth={2} data-testid="CommentContent">
      <Styled.CommentTitle>
        <Styled.AlignItems>
          {(type === CommentNotificationTypes.REASON_IDEA_SINGLE ||
            type === CommentNotificationTypes.REASON_IDEA_SAME_USER ||
            type === CommentNotificationTypes.REASON_USER_SINGLE ||
            type === CommentNotificationTypes.REASON_USER_SAME_USER ||
            type === CommentNotificationTypes.REASON_USER_DIFFERENT_CHALLENGES ||
            type === CommentNotificationTypes.REASON_TAG) &&
            (isValid ? (
              <Styled.LinkedTitle href={itemUrl} target="_blank" rel="noreferrer">
                {clearTitle}
              </Styled.LinkedTitle>
            ) : (
              <Tooltip content={message}>
                <Styled.LinkedTitleGreyout target="_blank" rel="noreferrer">
                  {clearTitle}
                </Styled.LinkedTitleGreyout>
              </Tooltip>
            ))}
          {type &&
            [CommentNotificationTypes.REASON_BLOG_SINGLE, CommentNotificationTypes.REASON_BLOG_SAME_USER].includes(
              type
            ) &&
            (isValid ? (
              <Styled.LinkedTitle href={itemUrl} target="_blank" rel="noreferrer">
                {clearTitle}
              </Styled.LinkedTitle>
            ) : (
              <Tooltip content={message}>
                <Styled.LinkedTitleGreyout target="_blank" rel="noreferrer">
                  {clearTitle}
                </Styled.LinkedTitleGreyout>
              </Tooltip>
            ))}
          {type === CommentNotificationTypes.REASON_IDEA_SAME_USER && (
            <>
              <FormattedMessage id="fromTheChallenge" defaultMessage="from the challenge" />
              {isValid ? (
                <Styled.LinkedTitle href={`/category/${challenge?.id}`} target="_blank" rel="noreferrer">
                  {clearChallengeTitle}
                </Styled.LinkedTitle>
              ) : (
                <Tooltip content={message}>
                  <Styled.LinkedTitleGreyout target="_blank" rel="noreferrer">
                    {clearChallengeTitle}
                  </Styled.LinkedTitleGreyout>
                </Tooltip>
              )}
            </>
          )}
          {(type === CommentNotificationTypes.REASON_IDEA_SAME_IDEA ||
            type === CommentNotificationTypes.REASON_USER_SAME_CHALLENGE ||
            type === CommentNotificationTypes.REASON_CHALLENGE_SINGLE ||
            type === CommentNotificationTypes.REASON_CHALLENGE_SAME_CHALLENGE ||
            type === CommentNotificationTypes.REASON_BLOG_DIFFERENT_USER) && (
            <Styled.AuthorWithImage>
              {url && <Styled.ProfileImage data-testid="UserProfileImage" src={url} />}
              <Styled.AuthorName
                href={`/user/activity/${author?.id}`}
                onClick={(e) =>
                  openModal(e, {
                    title: intl.formatMessage({ id: 'profile', defaultMessage: 'Profile' }),
                    url: `/user/view/${author?.id}`
                  })
                }
              >
                {author?.displayName}
              </Styled.AuthorName>
              <FormattedMessage id="hasCommented" defaultMessage="has commented:" />
            </Styled.AuthorWithImage>
          )}
        </Styled.AlignItems>
        {time && <RelativeDateWithTooltip time={time} />}
      </Styled.CommentTitle>
      {isValid ? (
        <Styled.CommentBody onClick={commentBodyClicked}>{removeTags(commentText)}</Styled.CommentBody>
      ) : (
        <Tooltip content={message}>
          <Styled.CommentBodyGreyout>{removeTags(commentText)}</Styled.CommentBodyGreyout>
        </Tooltip>
      )}
    </Styled.ContentContainer>
  );
};
